import React, { useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { CalendarX2, Globe, Instagram, Mic, Music2, Youtube } from 'lucide-react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import EventCard from '@/components/EventCard';
import { EmptyState, Img, Shimmer } from '@/components/ui';
import { useArtist } from '@/hooks/useCatalog';

const LINKS = [
  { key: 'instagramUrl', label: 'Instagram', icon: Instagram },
  { key: 'youtubeUrl', label: 'YouTube', icon: Youtube },
  { key: 'spotifyUrl', label: 'Spotify', icon: Music2 },
  { key: 'websiteUrl', label: 'Website', icon: Globe },
];

/**
 * One artist and every show they have on LocalKonnect, upcoming then past.
 * The events come from the lineups set on each event in LK Admin, so an
 * artist's page fills itself as they're added to events.
 */
const ArtistPage = () => {
  const { slug } = useParams();
  const { artist, loading } = useArtist(slug);

  useEffect(() => {
    if (artist?.name) document.title = `${artist.name} | LocalKonnect`;
  }, [artist?.name]);

  if (loading) {
    return (
      <div className="min-h-screen bg-surface-muted">
        <Header />
        <div className="lk-container flex flex-col items-center gap-4 py-12 sm:flex-row sm:items-end">
          <Shimmer className="h-36 w-36 rounded-full sm:h-44 sm:w-44" />
          <div className="w-full max-w-md space-y-3">
            <Shimmer className="mx-auto h-8 w-56 sm:mx-0" />
            <Shimmer className="mx-auto h-4 w-40 sm:mx-0" />
          </div>
        </div>
      </div>
    );
  }

  if (!artist) {
    return (
      <div className="flex min-h-screen flex-col bg-surface-muted">
        <Header />
        <div className="lk-container flex-1 py-20">
          <EmptyState
            icon={Mic}
            title="Artist not found"
            message="This artist may no longer be listed on LocalKonnect."
            action={
              <Link to="/" className="lk-btn-primary">
                Browse all events
              </Link>
            }
          />
        </div>
        <Footer />
      </div>
    );
  }

  const links = LINKS.filter((l) => artist[l.key]);
  const { upcoming, past } = artist;

  return (
    <div className="flex min-h-screen flex-col bg-surface-muted">
      <Header />

      <main className="flex-1">
        {/* Profile */}
        <section className="border-b border-ink-100 bg-white">
          <div className="lk-container flex flex-col items-center gap-6 py-10 text-center sm:flex-row sm:items-end sm:text-left">
            <Img
              src={artist.photo}
              alt={artist.name}
              label={artist.name}
              className="h-36 w-36 shrink-0 overflow-hidden rounded-full shadow-card ring-4 ring-white sm:h-44 sm:w-44"
            />
            <div className="min-w-0 flex-1">
              <p className="lk-eyebrow">Artist</p>
              <h1 className="text-h1 text-ink-900">{artist.name}</h1>
              {artist.tagline && <p className="mt-1 text-ink-500">{artist.tagline}</p>}
              <p className="mt-2 text-sm font-medium text-ink-700">
                {upcoming.length > 0 ? `${upcoming.length} upcoming` : 'No upcoming shows'}
                {past.length > 0 ? ` · ${past.length} past` : ''}
              </p>
              {links.length > 0 && (
                <div className="mt-4 flex flex-wrap justify-center gap-2 sm:justify-start">
                  {links.map(({ key, label, icon: Icon }) => (
                    <a
                      key={key}
                      href={artist[key]}
                      target="_blank"
                      rel="noreferrer noopener"
                      className="lk-chip lk-chip-idle min-h-[40px] gap-1.5"
                    >
                      <Icon className="h-4 w-4" aria-hidden="true" />
                      {label}
                    </a>
                  ))}
                </div>
              )}
            </div>
          </div>
          {artist.bio && (
            <div className="lk-container pb-8">
              <p className="max-w-3xl whitespace-pre-line text-sm leading-relaxed text-ink-700">{artist.bio}</p>
            </div>
          )}
        </section>

        <div className="lk-container py-8">
          <h2 className="lk-heading mb-6">Upcoming events</h2>
          {upcoming.length === 0 ? (
            <EmptyState
              icon={CalendarX2}
              title={`No upcoming shows from ${artist.name}`}
              message="New dates are added as they're announced — check back soon."
              action={
                <Link to="/" className="lk-btn-primary">
                  Explore other events
                </Link>
              }
            />
          ) : (
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
              {upcoming.map((event, i) => (
                <EventCard key={event.id} event={event} index={i} compact />
              ))}
            </div>
          )}

          {past.length > 0 && (
            <div className="mt-12">
              <h2 className="lk-heading mb-6">Past events</h2>
              <div className="grid grid-cols-2 gap-4 opacity-80 sm:grid-cols-3 lg:grid-cols-4">
                {past.map((event, i) => (
                  <EventCard key={event.id} event={event} index={i} compact />
                ))}
              </div>
            </div>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default ArtistPage;
